import type {
  AdminGame,
  ImportMediaRequest,
  SgdbImage,
  UpdateGameRequest,
} from '@mcgames/contracts';
import { Button, Dialog, Spinner } from '@mcgames/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import { useGameMutation, useRememberSgdbGame } from './game-hooks.ts';
import styles from './Games.module.css';
import type { ImageKind } from './media-kinds.ts';
import { SgdbSearch } from './SgdbSearch.tsx';

export type SgdbPickerProps = {
  game: AdminGame;
  kind: ImageKind;
  onClose: () => void;
};

/**
 * SteamGridDB flow: search a game, remember it on the game (`sgdbId`), pick one of its images of
 * `kind` and import it. A game that already has an `sgdbId` opens straight to the images.
 */
export function SgdbPicker({ game, kind, onClose }: SgdbPickerProps) {
  const { t } = useTranslation('admin');
  const kindLabel = t(`games.kinds.${kind}`);
  const [searching, setSearching] = useState(game.sgdbId === null);
  const pick = useGameMutation<UpdateGameRequest>(game.id, 'PATCH');
  const rememberSgdbGame = useRememberSgdbGame();

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('games.sgdb.title', { kind: kindLabel })}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('actions.close', { ns: 'common' })}
        </Button>
      }
    >
      {searching || game.sgdbId === null ? (
        <SgdbSearch
          initialQuery={game.title}
          busy={pick.isPending}
          error={pick.error}
          onPick={(result) =>
            pick.mutate(
              { sgdbId: result.id },
              {
                onSuccess: () => {
                  rememberSgdbGame(result);
                  setSearching(false);
                },
              },
            )
          }
        />
      ) : (
        <SgdbImages
          game={game}
          sgdbId={game.sgdbId}
          kind={kind}
          onSearchAgain={() => setSearching(true)}
          onImported={onClose}
        />
      )}
    </Dialog>
  );
}

type SgdbImagesProps = {
  game: AdminGame;
  sgdbId: number;
  kind: ImageKind;
  onSearchAgain: () => void;
  onImported: () => void;
};

function SgdbImages({ game, sgdbId, kind, onSearchAgain, onImported }: SgdbImagesProps) {
  const { t } = useTranslation('admin');
  const images = useApiQuery<SgdbImage[]>(
    `/api/admin/metadata/steamgriddb/${sgdbId}/images?kind=${kind}`,
  );
  const importImage = useGameMutation<ImportMediaRequest>(game.id, 'POST', '/media/import');

  return (
    <>
      <p className={styles['hint']}>
        {t('games.sgdb.imagesHint', { kind: t(`games.kinds.${kind}`) })}
      </p>
      <div className={styles['row']}>
        <Button variant="secondary" size="sm" onClick={onSearchAgain}>
          {t('games.sgdb.searchAgain')}
        </Button>
      </div>
      {images.isPending && !images.isError && <Spinner />}
      <ErrorText error={images.error ?? importImage.error} />
      {images.data?.length === 0 && <p className={styles['muted']}>{t('games.sgdb.noImages')}</p>}
      {images.data !== undefined && images.data.length > 0 && (
        <ul className={styles['imageGrid']}>
          {images.data.map((image) => (
            <li key={image.url}>
              <button
                type="button"
                className={styles['imageButton']}
                disabled={importImage.isPending}
                aria-label={t('games.sgdb.importImage', {
                  width: image.width,
                  height: image.height,
                })}
                onClick={() =>
                  importImage.mutate({ url: image.url, kind }, { onSuccess: onImported })
                }
              >
                <img className={styles['imageThumb']} src={image.thumbUrl} alt="" loading="lazy" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {importImage.isPending && <p className={styles['muted']}>{t('games.sgdb.importing')}</p>}
    </>
  );
}
